import React, { useState, useEffect } from 'react';
import { validateAndFixJson, convertToDiscoverFormat } from '../../utils/jsonValidator';
import { generateDiscoverJson, formatJsonOutput } from '../../utils/jsonGenerator';

// This component provides tools to diagnose and fix issues with discover.json
const DiscoverJsonFixer = () => {
  const [originalJson, setOriginalJson] = useState('');
  const [fixedJson, setFixedJson] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [jsonLocations, setJsonLocations] = useState([
    '/public/discover.json',
    '/src/data/discover.json',
    '/src/Legacy/discover.json',
    '/src/Legacy/discover_backup.json',
    '/src/manual_discover.json'
  ]);
  const [selectedLocation, setSelectedLocation] = useState('/public/discover.json');
  const [itemCount, setItemCount] = useState(50);

  // Load the current JSON file
  const loadJsonFile = async () => {
    setStatus('loading');
    setMessage('Loading JSON file...');
    
    try {
      const response = await fetch(selectedLocation)
        .catch(() => null);
      
      if (response && response.ok) {
        const content = await response.text();
        setOriginalJson(content);
        setMessage('File loaded successfully.');
        validateJson(content);
      } else {
        setOriginalJson('');
        setMessage(`File not found at ${selectedLocation}`);
        setStatus('error');
      }
    } catch (error) {
      console.error('Error loading JSON file:', error);
      setOriginalJson('');
      setMessage(`Error loading file: ${error.message}`);
      setStatus('error');
    }
  };

  // Validate and attempt to fix the JSON
  const validateJson = (jsonString) => {
    try {
      setMessage('Validating JSON...');
      
      // Use the utility function from jsonValidator.js
      const validationResult = validateAndFixJson(jsonString);
      
      if (validationResult.valid) {
        setMessage('JSON is valid. Converting to correct format...');
        
        // Convert to the correct format for discover.json
        const jsonObject = JSON.parse(validationResult.json);
        const formattedJson = convertToDiscoverFormat(jsonObject);
        
        // Format for display
        const prettyJson = JSON.stringify(formattedJson, null, 2);
        setFixedJson(prettyJson);
        setStatus('success');
        setMessage('JSON has been validated and formatted correctly.');
      } else {
        setFixedJson(validationResult.json);
        setStatus('error');
        setMessage(`JSON validation failed: ${validationResult.message}`);
      }
    } catch (error) {
      console.error('Error validating JSON:', error);
      setStatus('error');
      setMessage(`Error validating JSON: ${error.message}`);
    }
  };

  // Generate new JSON data
  const generateNewJson = () => {
    try {
      setStatus('loading');
      setMessage(`Generating new discover.json with ${itemCount} items...`);
      
      // Use the utility function from jsonGenerator.js
      const discoverData = generateDiscoverJson(itemCount);
      const jsonString = formatJsonOutput(discoverData);
      
      setFixedJson(jsonString);
      setStatus('success');
      setMessage('New JSON generated successfully. You can save this to replace your current file.');
    } catch (error) {
      console.error('Error generating JSON:', error);
      setStatus('error');
      setMessage(`Error generating JSON: ${error.message}`);
    }
  };

  // Save the fixed/generated JSON
  const saveJsonFile = () => {
    if (!fixedJson) {
      setMessage('No fixed JSON to save.');
      return;
    }
    
    // Create a download link
    const blob = new Blob([fixedJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'discover.json';
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
    
    setMessage('JSON file downloaded. Please save it to the correct location in your project.');
  };

  // Check all possible locations for existing JSON files
  useEffect(() => {
    const checkJsonLocations = async () => {
      const validLocations = [];
      
      for (const location of jsonLocations) {
        try {
          const response = await fetch(location, { method: 'HEAD' });
          if (response.ok) {
            validLocations.push(location);
          }
        } catch (error) {
          // File doesn't exist or can't be read, skip
        }
      }
      
      if (validLocations.length > 0) {
        setJsonLocations(validLocations);
        setSelectedLocation(validLocations[0]);
      }
    };
    
    checkJsonLocations();
  }, []);

  return (
    <div className="discover-json-fixer">
      <h2>Discover JSON Fixer</h2>
      
      <div className="fixer-description">
        <p>
          This utility helps diagnose and fix issues with your discover.json file,
          which is causing the "Unexpected token '&lt;'" error in your application.
        </p>
      </div>
      
      <div className="file-selector">
        <label htmlFor="location-select">JSON File Location:</label>
        <select 
          id="location-select"
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
        >
          {jsonLocations.map(location => (
            <option key={location} value={location}>{location}</option>
          ))}
        </select>
        
        <button 
          onClick={loadJsonFile}
          disabled={status === 'loading'}
          className="action-button"
        >
          Load File
        </button>
      </div>
      
      <div className="status-message" data-status={status}>
        {message}
      </div>
      
      {originalJson && (
        <div className="json-preview">
          <h3>Original JSON</h3>
          <pre className="json-content">{originalJson.slice(0, 500)}...</pre>
        </div>
      )}
      
      {fixedJson && (
        <div className="json-preview">
          <h3>Fixed JSON</h3>
          <pre className="json-content">{fixedJson.slice(0, 500)}...</pre>
          
          <button 
            onClick={saveJsonFile}
            className="action-button save-button"
          >
            Download Fixed JSON
          </button>
        </div>
      )}
      
      <div className="generator">
        <h3>Generate New JSON</h3>
        <p>
          If fixing the existing JSON doesn't work, you can generate a new discover.json file
          with sample data.
        </p>
        
        <div className="generator-controls">
          <label htmlFor="item-count">Number of items:</label>
          <input 
            type="number" 
            id="item-count" 
            min="10" 
            max="200" 
            value={itemCount}
            onChange={(e) => setItemCount(parseInt(e.target.value) || 50)}
          />
          
          <button 
            onClick={generateNewJson}
            className="action-button generate-button"
          >
            Generate New JSON
          </button>
        </div>
      </div>
      
      <div className="instructions">
        <h3>How to Fix Your discover.json Issue</h3>
        <ol>
          <li>Click "Load File" to analyze your current discover.json</li>
          <li>If it can be fixed, the corrected version will appear below</li>
          <li>Click "Download Fixed JSON" to download the corrected file</li>
          <li>Replace your existing discover.json with this downloaded file</li>
          <li>If fixing doesn't work, use "Generate New JSON" to create a new file with sample data</li>
        </ol>
      </div>
    </div>
  );
};

export default DiscoverJsonFixer;